import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Home,
  User as UserIcon,
  Upload,
  FileText,
  Briefcase,
  CheckCircle,
  XCircle,
  PlusCircle,
  Menu,
  X,
  Users,
  Inbox,
  Building2,
} from "lucide-react";
import Wordmark from "../Brand/Wordmark";

interface SidebarProps {
  userType: "jobseeker" | "hr" | "admin";
}

interface NavigationItem {
  path: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  label: string;
  badge?: string;
  section?: string;
}

const SIDEBAR_STATE_KEY = "velaigal.ui.sidebar.expanded";

const cn = (
  ...classes: Array<string | false | null | undefined>
): string => classes.filter(Boolean).join(" ");

/*
 * Velaigal post-auth sidebar.
 * Cream bg, hairline right border, no purple gradient, no glass blur.
 * Active route uses saffron-tint bg + saffron left border accent. Per spec §4.9.
 */
const Sidebar: React.FC<SidebarProps> = ({ userType }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(SIDEBAR_STATE_KEY);
    return stored ? JSON.parse(stored) : true;
  });
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(isExpanded));
  }, [isExpanded]);

  const navigationItems: NavigationItem[] = useMemo(() => {
    switch (userType) {
      case "jobseeker":
        return [
          { path: "/jobs", icon: Briefcase, label: "Jobs" },
          { path: "/job-seeker/applications", icon: FileText, label: "Applications" },
          { path: "/job-seeker/profile", icon: UserIcon, label: "Profile" },
          { path: "/job-seeker/resume", icon: Upload, label: "Resume" },
        ];
      case "hr":
        return [
          { path: "/hr/create-job", icon: PlusCircle, label: "Create job" },
          { path: "/hr/jobs", icon: Briefcase, label: "My jobs" },
          { path: "/hr/shortlisted", icon: CheckCircle, label: "Shortlisted" },
          { path: "/hr/rejected", icon: XCircle, label: "Rejected" },
          { path: "/hr/profile", icon: UserIcon, label: "Profile" },
        ];
      case "admin":
        return [
          { path: "/admin", icon: Home, label: "Dashboard" },
          { path: "/admin/users", icon: Users, label: "Users" },
          { path: "/admin/companies", icon: Building2, label: "Companies" },
          { path: "/admin/requests", icon: Inbox, label: "Requests" },
        ];
      default:
        return [];
    }
  }, [userType]);

  return (
    <aside
      className={cn(
        "relative flex min-h-screen flex-col bg-cream border-r border-hairline transition-[width] duration-200 ease-velaigal",
        isExpanded ? "w-60" : "w-16"
      )}
    >
      <div
        className={cn(
          "flex items-center py-6",
          isExpanded ? "justify-between px-5" : "justify-center px-2"
        )}
      >
        <Link
          to="/"
          aria-label="Velaigal"
          className={cn(
            "no-underline text-ink transition-opacity duration-200",
            isExpanded ? "opacity-100" : "opacity-0 pointer-events-none w-0 overflow-hidden"
          )}
          style={{ textDecoration: "none" }}
        >
          <Wordmark variant="lockup" size="sm" animateOnMount />
        </Link>
        <button
          type="button"
          onClick={() => setIsExpanded((previous) => !previous)}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center text-ink-tertiary hover:text-ink transition-colors"
          aria-label={isExpanded ? "Collapse navigation" : "Expand navigation"}
          title={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <X className="h-4 w-4" strokeWidth={1.75} />
          ) : (
            <Menu className="h-4 w-4" strokeWidth={1.75} />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="px-5 mb-3">
          <span className="text-[11px] uppercase tracking-widest text-hairline-strong font-medium">
            {userType === "hr" ? "Recruiter" : userType === "admin" ? "Admin" : "Workspace"}
          </span>
        </div>
      )}

      <nav className="flex-1 px-2 space-y-1">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          // "/admin" is a prefix of "/admin/users" and "/admin/requests", so the
          // dashboard's root path needs an exact match — otherwise it'd light up
          // alongside whichever admin sub-page is actually active.
          const isActive =
            item.path === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              title={!isExpanded ? item.label : undefined}
              className={cn(
                "group relative flex items-center gap-3 rounded text-sm font-medium transition-colors no-underline",
                isExpanded ? "px-3 py-2.5" : "px-2 py-2.5 justify-center",
                isActive
                  ? "bg-saffron-tint text-ink"
                  : "text-ink-secondary hover:bg-[#EDE7D7] hover:text-ink"
              )}
              style={{ textDecoration: "none" }}
            >
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-saffron rounded-r"
                />
              )}
              <Icon
                className={cn(
                  "h-[18px] w-[18px] flex-none",
                  isActive ? "text-saffron" : "text-ink-tertiary group-hover:text-ink"
                )}
                strokeWidth={1.75}
              />
              {isExpanded && (
                <div className="flex w-full items-center justify-between gap-2">
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest rounded bg-saffron text-cream">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </Link>
          );
        })}
      </nav>

      {isExpanded && (
        <div className="px-5 py-5 mt-auto border-t border-hairline">
          <span className="text-[11px] uppercase tracking-widest text-hairline-strong font-medium block mb-1">
            Velaigal
          </span>
          <span className="text-xs text-ink-tertiary block">
            Hiring built on what people can actually do.
          </span>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
